import React from 'react';
import { Modal, Button, Tabs, Row, Col } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import Spinner from '../Spinner';
import './RoomModal.less';
import {
  CSV_MAPPING,
  ROOM_MODAL_TAB_CSV_HEADERS,
  ROOM_MODAL_TAB_KEYS,
  TIME_AND_DATE_FORMATS
} from '@app/constants';
import { intervalFormatter, timeFormatter } from '@app/utils/helpers';
import { MeasurementsInterface, RoomModalInterface } from '@app/types/rooms';
import MeasurementsGraph from '../MeasurementsGraphic';
import { AlarmsStatusIndicator } from '@app/common/AlarmsModal/components/AlarmsStatusIndicator';
import CsvReportDownload from '@app/common/CsvReportDownload';

const { TabPane } = Tabs;

const RoomModal = ({ isModalVisible, handleOk, data, loading, onTabChange }: RoomModalInterface) => {
  const onChange = key => {
    onTabChange(key);
  };
  dayjs.extend(utc);
  dayjs.extend(relativeTime);

  const generateCSVDataFromReports = (reports: Array<MeasurementsInterface>) => {
    if (!reports) return [];
    return reports.reduce((acc, item) => {
      let tempStorage = {
        roomName: data?.extraData?.roomName,
        buildingName: data?.extraData?.buildingName,
        levelName: data?.extraData?.levelName
      };
      Object.keys(item).forEach(key => {
        if (ROOM_MODAL_TAB_CSV_HEADERS.includes(key)) {
          switch (key) {
            case 'dt':
              tempStorage[CSV_MAPPING[key]] = intervalFormatter(item[key]); // GMT + 4
              break;
            case 'date':
              tempStorage[CSV_MAPPING[key]] = String(item[key] / 1000);
              break;
            case 'fanTime':
            case 'compTime':
            case 'presenceTime':
            case 'conTime':
              const duration = dayjs.duration(item[key], 'seconds');
              tempStorage[CSV_MAPPING[key]] = duration.format('mm[m] ss[s]');
              break;
            case 'energy':
            default:
              tempStorage[CSV_MAPPING[key]] = String(item[key] || '');
              break;
          }
        }
      });
      acc.push(tempStorage);
      return acc;
    }, [] as Array<any>);
  };

  const csvData = generateCSVDataFromReports(data?.measurements);

  return (
    <Modal
      centered
      className="room-modal"
      closable={false}
      destroyOnClose={true}
      open={isModalVisible}
      onOk={handleOk}
      footer={[
        <Button key="submit" type="primary" onClick={handleOk}>
          OK
        </Button>
      ]}
    >
      <>
        <div className="room-modal-header">
          <div>{data?.extraData?.roomName || '-'}</div>

          <CsvReportDownload
            csvData={csvData}
            fileName={`[${data?.extraData?.buildingName}][${data?.extraData?.roomName}] room data`}
          />

        </div>

        <Tabs defaultActiveKey="1" onChange={onChange}>
          <TabPane tab="Historical Measurements" key={ROOM_MODAL_TAB_KEYS.Measurements}>
            {loading ? <Spinner /> : <MeasurementsGraph measurements={data?.measurements} />}
          </TabPane>
          <TabPane tab="Current Alarm" key={ROOM_MODAL_TAB_KEYS.Alarm}>
            {loading ? (
              <Spinner />
            ) : (
              <Row>
                <Col span={24}>
                  <div className="my-2">
                    <h3>Recent Alarms</h3>
                    <ul className="alarms-block">
                      {data?.alarms?.length ? (
                        data.alarms.map((alarm, i) => (
                          <div className="status-indicator" key={`${alarm.timestamp}-${i}`}>
                            <AlarmsStatusIndicator isCleared={!alarm.status} />
                            <div className="alarm-info">
                              <span className="alarm-type">{alarm.type}</span>
                              <span className="alarm-time">
                                {timeFormatter(alarm.timestamp * 1000, TIME_AND_DATE_FORMATS.FULL_DATE_TIME_FORMAT)}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div>No alarms detected</div>
                      )}
                    </ul>
                  </div>
                </Col>
              </Row>
            )}
          </TabPane>
          <TabPane tab="Admin Data" key={ROOM_MODAL_TAB_KEYS.Data}>
            {loading ? (
              <Spinner />
            ) : (
              <Row>
                <Col span={12}>
                  <div className="my-2">
                    <h3>Room Name</h3>
                    <h4>{data?.roomInfo?.name || '-'}</h4>
                  </div>
                  <div className="my-2">
                    <h3>SubTenant</h3>
                    <h4>{data?.roomInfo?.subtenantName || '-'}</h4>
                  </div>
                  <div className="my-2">
                    <h3>Firmware version</h3>
                    <h4> - </h4>
                  </div>
                  <div className="my-2">
                    <h3>SSID for connection</h3>
                    <h4> - </h4>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="my-2">
                    <h3>Room Size</h3>
                    <h4>{data?.roomInfo?.size || '-'}</h4>
                  </div>
                  <div className="my-2">
                    <h3>Next presence off event</h3>
                    <h4> - </h4>
                  </div>
                  <div className="my-2">
                    <h3>Wifi RSSL level</h3>
                    <h4> - </h4>
                  </div>
                </Col>
              </Row>
            )}
          </TabPane>
        </Tabs>
      </>
    </Modal>
  );
};

export default RoomModal;
